import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSessionExtensionForm from "src/components/admin/enrollments/AdminSessionExtensionForm";
import { Button } from "src/components/ui/Button";
import { useToast } from "src/hooks/use-toast";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import {
  BatchExtensionContext,
  manualEnrollmentService,
} from "src/services/manualEnrollmentService";

const ExtendBatchSessions: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setTitle, setDescription, reset } = usePageHeaderStore();
  const [context, setContext] = useState<BatchExtensionContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!batchId) return;
    setIsLoading(true);
    try {
      setContext(await manualEnrollmentService.fetchBatchExtensionContext(batchId));
    } catch (error) {
      toast({
        title: "Unable to load batch",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [batchId, toast]);

  useEffect(() => {
    setTitle("Add Sessions");
    setDescription("Extend an active batch after verifying payment and its invoice");
    void load();
    return () => reset();
  }, [load, reset, setDescription, setTitle]);

  if (isLoading) return <p className="py-8 text-center text-gray-500">Loading batch…</p>;

  return (
    <div className="space-y-6">
      <Button type="button" variant="outline" onClick={() => navigate("/admin/batches/view")}>
        <ArrowLeft className="mr-1 h-4 w-4" />Back to batches
      </Button>
      {context ? (
        <AdminSessionExtensionForm
          subscriptionId={context.subscription_id}
          studentId={context.student_id}
          currency={context.currency}
          defaultPricePerSession={context.actual_price_per_session}
          onExtended={load}
        />
      ) : (
        <p className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
          This batch cannot currently be extended.
        </p>
      )}
    </div>
  );
};

export default ExtendBatchSessions;
